/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useRef, useContext, memo } from 'react';

import { useSearchParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import classNames from 'classnames';

import Question from '../../Question';
import ReplyBox from '../../ReplyBox';
import Comment from '../../Comment';
import Switch from '../../Switch';
import FilledButton from '../../Buttons/FilledButton';
import Transcript from './Transcript';

import { UiContext } from '../../../context/UiContext';
import CommentService from '../../../services/CommentService';
import MeetingService from '../../../services/MeetingService';
import classes from './styles.module.scss';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import downloadIcon from '../../../assets/images/reports/download.svg';
import downloadFile from '../../../helpers/downloadFile';

function Comments({
  commentsSource,
  questions,
  user,
  refetchMeeting,
  videoRef,
  id,
  setIsSignUpPopupVisible,
  rewindVideoByComment,
  isAuthenticated,
  shouldNotify,
  refetchComments,
  transcript,
  setIsPlaying,
  signedS3Url,
  meeting,
  title,
  videoDuration,
  isPlaying,
  audioSectionsWithoutBackground,
  miniVideoRef,
  currentTime,
}) {
  const [commentText, setCommentText] = useState('');
  const [commentsMode, setCommentsMode] = useState('engagements');
  const [areNotificationsEnabled, setAreNotificationsEnabled] = useState(null);

  const textareaRef = useRef();

  const { notifyError } = useContext(UiContext);

  const [searchParams] = useSearchParams();

  const isMobile = useCheckMobileScreen();

  const { mutate: createCommentMutation } = useMutation(
    CommentService.createComment,
    {
      onSuccess: () => {
        textareaRef.current.textContent = '';
        refetchComments();
      },
      onError: (error) => {
        console.error(error);
        // notifyError(error?.response?.data?.message);
      },
    }
  );

  const { mutate: toggleNotifications } = useMutation(
    MeetingService.toggleMeetingNotifications,
    {
      onSuccess: () => {
        refetchMeeting();
      },
      onError: (error) => {
        console.error(error);
        notifyError(error?.response?.data?.message);
      },
    }
  );

  const { mutate: downloadQuestionnaire, isLoading } = useMutation({
    mutationFn: () => MeetingService.downloadMeetingQuestionnaire(id),
    onSuccess: (response) => {
      downloadFile(response, 'questionnaire.xlsx');
    },
    onError: (error) => {
      console.error(error);
      notifyError(error?.response?.data?.message);
    },
  });

  useEffect(() => {
    if (shouldNotify !== undefined) {
      setAreNotificationsEnabled(shouldNotify);
    }
  }, [shouldNotify]);

  useEffect(() => {
    if (
      shouldNotify !== areNotificationsEnabled &&
      shouldNotify !== undefined &&
      areNotificationsEnabled !== null
    ) {
      toggleNotifications(id);
    }
  }, [areNotificationsEnabled, id, toggleNotifications, shouldNotify]);

  const createComment = () => {
    if (commentText.trim()) {
      createCommentMutation({
        textContent: commentText,
        meetingId: id,
        meetingTime: currentTime,
      });
    }
  };

  useEffect(() => {
    if (searchParams.get('responses')) {
      setCommentsMode('responses');
    }
  }, [searchParams, setCommentsMode]);

  return (
    <div
      className={classNames(classes.Comments, {
        [classes.transcript]: commentsMode === 'transcript',
      })}
    >
      <div className={classes.container}>
        <header className={classes.header}>
          {(!isMobile || commentsMode !== 'transcript') && (
            <div className={classes.tabs}>
              <div
                className={classNames(
                  classes.tab,
                  classes.engagements,
                  commentsMode === 'engagements' && classes.active
                )}
                onClick={() => setCommentsMode('engagements')}
              >
                <span>Comments</span>
              </div>
              <div
                className={classNames(
                  classes.tab,
                  classes.responses,
                  commentsMode === 'responses' && classes.active
                )}
                onClick={() => setCommentsMode('responses')}
              >
                <span>Question Responses</span>
              </div>
              {!isMobile && transcript && (
                <div
                  className={classNames(
                    classes.tab,
                    classes.meetingTimeranscript,
                    commentsMode === 'transcript' && classes.active
                  )}
                  onClick={() => setCommentsMode('transcript')}
                >
                  <span>Transcript</span>
                </div>
              )}
            </div>
          )}
          <div className={classes.switchContainer}>
            <Switch
              onClick={() =>
                setAreNotificationsEnabled((prevState) => !prevState)
              }
              label="Notify me"
              width={isMobile ? 60 : 88}
              withOnLabel
              tooltipText="Notify me when people interact with this video"
              isChecked={areNotificationsEnabled}
            />
            {isMobile && transcript && (
              <button
                type="button"
                className={classes.showTranscriptButton}
                onClick={() => setCommentsMode('transcript')}
              >
                Show Transcript
              </button>
            )}
          </div>
        </header>
        {commentsMode === 'engagements' && (
          <ReplyBox
            textareaRef={textareaRef}
            user={user}
            commentText={commentText}
            setCommentText={setCommentText}
            createComment={createComment}
            setIsSignUpPopupVisible={setIsSignUpPopupVisible}
          />
        )}
        {commentsMode === 'engagements' && (
          <div className={classes.commentsList}>
            {commentsSource?.Comments?.map((comment) => (
              <Comment
                key={comment.id}
                comment={comment}
                createCommentMutation={createCommentMutation}
                rewindVideoByComment={rewindVideoByComment}
                user={user}
                refetch={refetchComments}
                meetingId={id}
                isAuthenticated={isAuthenticated}
                setIsSignUpPopupVisible={setIsSignUpPopupVisible}
              />
            ))}
          </div>
        )}
        {commentsMode === 'responses' && (
          <>
            {questions.length ? (
              <div className={classes.questionsList}>
                <div className={classes.downloadButtonContainer}>
                  <FilledButton
                    onClick={downloadQuestionnaire}
                    isDisabled={isLoading}
                    width={isMobile ? '100%' : 196}
                  >
                    Export{' '}
                    <img
                      src={downloadIcon}
                      alt="Export"
                      className={classes.exportIcon}
                    />
                  </FilledButton>
                </div>
                {questions?.map((question) => (
                  <Question question={question} key={question.id} />
                ))}
              </div>
            ) : (
              <p className={classes.noQuestions}>
                There are no questions in this video
              </p>
            )}
          </>
        )}

        <div
          style={{
            display: commentsMode === 'transcript' ? 'block' : 'none',
          }}
        >
          <Transcript
            transcript={transcript}
            setCommentsMode={setCommentsMode}
            videoRef={videoRef}
            setIsPlaying={setIsPlaying}
            signedS3Url={signedS3Url}
            meeting={meeting}
            title={title}
            videoDuration={videoDuration}
            isPlaying={isPlaying}
            audioSectionsWithoutBackground={audioSectionsWithoutBackground}
            miniVideoRef={miniVideoRef}
          />
        </div>
      </div>
    </div>
  );
}

export default memo(Comments);
